import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Plus, Trash2, Save, GripVertical, BookOpen, ClipboardList, FileQuestion, X, CheckCircle2, FolderTree } from "lucide-react";

const emptyQuestion = () => ({ question: "", options: ["", "", "", ""], correct_index: 0 });

export default function CourseEditor() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();

  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [modules, setModules] = useState([]);
  const [initialExam, setInitialExam] = useState(null);
  const [finalExam, setFinalExam] = useState(null);
  const [tab, setTab] = useState("lessons");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [newLesson, setNewLesson] = useState({ title: "", content: "", video_url: "", order_index: 0, module_id: "" });
  const [newModuleTitle, setNewModuleTitle] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const load = async () => {
    try {
      const c = await api.get(`/courses/${courseId}`);
      if (user?.role === "instructor" && c.data.teacher_id !== user.id) {
        nav("/instructor");
        return;
      }
      setCourse(c.data);
      setLessons(c.data.lessons || []);
      setModules(c.data.modules || []);
      setNewLesson((n) => ({ ...n, order_index: (c.data.lessons || []).length }));
      const [ie, fe] = await Promise.all([
        api.get(`/courses/${courseId}/exams/initial`).catch(() => ({ data: null })),
        api.get(`/courses/${courseId}/exams/final`).catch(() => ({ data: null })),
      ]);
      setInitialExam(ie.data ? { passing_score: ie.data.passing_score, questions: ie.data.questions.map((q) => ({ ...q, correct_index: q.correct_index ?? 0 })) } : { passing_score: 0, questions: [emptyQuestion()] });
      setFinalExam(fe.data ? { passing_score: fe.data.passing_score, questions: fe.data.questions.map((q) => ({ ...q, correct_index: q.correct_index ?? 0 })) } : { passing_score: 80, questions: [emptyQuestion()] });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [courseId]);

  const flash = (text, isErr = false) => {
    if (isErr) { setErr(text); setMsg(""); } else { setMsg(text); setErr(""); }
    setTimeout(() => { setMsg(""); setErr(""); }, 2500);
  };

  // ------- Modules -------
  const addModule = async (e) => {
    e.preventDefault();
    if (!newModuleTitle.trim()) return;
    try {
      await api.post(`/courses/${courseId}/modules`, { title: newModuleTitle, order_index: modules.length });
      setNewModuleTitle("");
      flash("Módulo creado");
      load();
    } catch (e) { flash(formatApiError(e.response?.data?.detail), true); }
  };

  const saveModule = async (m) => {
    try {
      await api.put(`/modules/${m.id}`, { title: m.title, order_index: m.order_index || 0 });
      flash("Módulo actualizado");
    } catch (e) { flash(formatApiError(e.response?.data?.detail), true); }
  };

  const deleteModule = async (id) => {
    if (!window.confirm("¿Eliminar este módulo? Sus lecciones quedarán sin agrupar.")) return;
    try {
      await api.delete(`/modules/${id}`);
      flash("Módulo eliminado");
      load();
    } catch (e) { flash(formatApiError(e.response?.data?.detail), true); }
  };

  const updateModuleField = (idx, field, value) => {
    const copy = [...modules];
    copy[idx] = { ...copy[idx], [field]: value };
    setModules(copy);
  };

  // ------- Lessons -------
  const addLesson = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...newLesson, module_id: newLesson.module_id || null };
      await api.post(`/courses/${courseId}/lessons`, payload);
      setNewLesson({ title: "", content: "", video_url: "", order_index: lessons.length + 1, module_id: newLesson.module_id });
      flash("Lección creada");
      load();
    } catch (e) { flash(formatApiError(e.response?.data?.detail), true); }
  };

  const saveLesson = async (l) => {
    try {
      await api.put(`/lessons/${l.id}`, {
        title: l.title, content: l.content, video_url: l.video_url || "",
        order_index: l.order_index || 0, module_id: l.module_id || null,
      });
      flash("Lección actualizada");
    } catch (e) { flash(formatApiError(e.response?.data?.detail), true); }
  };

  const deleteLesson = async (id) => {
    if (!window.confirm("¿Eliminar esta lección?")) return;
    try {
      await api.delete(`/lessons/${id}`);
      flash("Lección eliminada");
      load();
    } catch (e) { flash(formatApiError(e.response?.data?.detail), true); }
  };

  const updateLessonField = (idx, field, value) => {
    const copy = [...lessons];
    copy[idx] = { ...copy[idx], [field]: value };
    setLessons(copy);
  };

  // ------- Drag & Drop reorder -------
  const sortedLessons = [...lessons].sort((a, b) => (a.order_index || 0) - (b.order_index || 0));

  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (e, idx) => {
    e.preventDefault();
    if (idx !== overIdx) setOverIdx(idx);
  };
  const onDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  const onDrop = async (e, dropIdx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === dropIdx) { onDragEnd(); return; }
    const items = [...sortedLessons];
    const [moved] = items.splice(dragIdx, 1);
    items.splice(dropIdx, 0, moved);
    // Optimistic update
    const reindexed = items.map((l, i) => ({ ...l, order_index: i }));
    setLessons(reindexed);
    onDragEnd();
    try {
      await api.post(`/courses/${courseId}/lessons/reorder`, { lesson_ids: items.map((l) => l.id) });
      flash("Orden actualizado");
    } catch (err) {
      flash(formatApiError(err.response?.data?.detail) || err.message, true);
      load(); // rollback
    }
  };

  // ------- Exams -------
  const currentExam = tab === "initial" ? initialExam : tab === "final" ? finalExam : null;
  const setCurrentExam = tab === "initial" ? setInitialExam : setFinalExam;

  const updateQuestionField = (qIdx, field, value) => {
    const copy = { ...currentExam };
    copy.questions = copy.questions.map((q, i) => (i === qIdx ? { ...q, [field]: value } : q));
    setCurrentExam(copy);
  };
  const updateOption = (qIdx, oIdx, value) => {
    const copy = { ...currentExam };
    copy.questions = copy.questions.map((q, i) => {
      if (i !== qIdx) return q;
      const opts = [...q.options];
      opts[oIdx] = value;
      return { ...q, options: opts };
    });
    setCurrentExam(copy);
  };
  const addQuestion = () => setCurrentExam({ ...currentExam, questions: [...currentExam.questions, emptyQuestion()] });
  const removeQuestion = (idx) => setCurrentExam({ ...currentExam, questions: currentExam.questions.filter((_, i) => i !== idx) });
  const addOption = (qIdx) => {
    const copy = { ...currentExam };
    copy.questions = copy.questions.map((q, i) => (i === qIdx ? { ...q, options: [...q.options, ""] } : q));
    setCurrentExam(copy);
  };
  const removeOption = (qIdx, oIdx) => {
    const copy = { ...currentExam };
    copy.questions = copy.questions.map((q, i) => {
      if (i !== qIdx) return q;
      if (q.options.length <= 2) return q;
      const opts = q.options.filter((_, k) => k !== oIdx);
      const ci = q.correct_index >= opts.length ? 0 : q.correct_index;
      return { ...q, options: opts, correct_index: ci };
    });
    setCurrentExam(copy);
  };

  const saveExam = async () => {
    try {
      // Validation
      for (const q of currentExam.questions) {
        if (!q.question.trim()) throw new Error("Todas las preguntas deben tener enunciado");
        if (q.options.length < 2) throw new Error("Cada pregunta requiere al menos 2 opciones");
        if (q.options.some((o) => !o.trim())) throw new Error("Todas las opciones deben tener texto");
      }
      await api.post(`/courses/${courseId}/exams`, {
        type: tab,
        passing_score: parseInt(currentExam.passing_score) || 0,
        questions: currentExam.questions,
      });
      flash(`Examen ${tab === "initial" ? "diagnóstico" : "final"} guardado`);
    } catch (e) { flash(formatApiError(e.response?.data?.detail) || e.message, true); }
  };

  if (!course) return <div className="p-10 text-marino">Cargando editor...</div>;

  const backTo = user?.role === "admin" ? "/admin" : "/instructor";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <Link to={backTo} data-testid="back-to-panel" className="inline-flex items-center gap-1 text-marino/70 hover:text-marino text-sm mb-3">
        <ArrowLeft className="w-4 h-4" /> Volver al panel
      </Link>
      <div className="flex flex-wrap justify-between items-end gap-3 mb-6">
        <div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl text-marino tracking-tight">Editor del curso</h1>
          <p className="text-marino/70 mt-1">{course.title}</p>
        </div>
        {(msg || err) && (
          <div data-testid="editor-flash" className={`px-3 py-2 rounded-lg text-sm ${err ? "bg-red-100 text-red-700 border border-red-200" : "bg-green-100 text-green-800 border border-green-200"}`}>
            {err || msg}
          </div>
        )}
      </div>

      <div className="border-b border-gris/30 flex flex-wrap gap-4 mb-6">
        {[
          { id: "lessons", label: "Lecciones", icon: BookOpen },
          { id: "initial", label: "Examen diagnóstico", icon: ClipboardList },
          { id: "final", label: "Examen final", icon: FileQuestion },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`editor-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 pb-2 font-medium transition-colors ${tab === t.id ? "text-naranja border-b-2 border-naranja" : "text-marino/60 hover:text-marino"}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Lecciones */}
      {tab === "lessons" && (
        <div className="space-y-6">
          {/* Modules manager */}
          <div className="bg-marino/5 border border-marino/10 rounded-xl p-5">
            <h3 className="font-heading font-bold text-marino text-lg mb-3 flex items-center gap-2"><FolderTree className="w-5 h-5 text-naranja" /> Módulos del curso</h3>
            <p className="text-xs text-marino/70 mb-3">Agrupa lecciones en módulos temáticos (opcional). Las lecciones sin módulo aparecerán como "General".</p>
            <form onSubmit={addModule} className="flex gap-2 mb-3">
              <input
                data-testid="new-module-title"
                value={newModuleTitle}
                onChange={(e) => setNewModuleTitle(e.target.value)}
                placeholder="Nombre del nuevo módulo (ej: Módulo 1 · Introducción)"
                className="flex-1 px-3 py-2 border border-gris rounded-lg text-sm bg-white"
              />
              <button data-testid="add-module-btn" className="bg-naranja text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-naranjaHover">
                <Plus className="w-4 h-4 inline" /> Añadir módulo
              </button>
            </form>
            {modules.length > 0 && (
              <div className="space-y-2">
                {modules.map((m, i) => (
                  <div key={m.id} data-testid={`module-row-${m.id}`} className="flex items-center gap-2 bg-white border border-gris/30 rounded-lg p-2">
                    <div className="w-7 h-7 rounded bg-marino text-white flex items-center justify-center text-xs font-bold">{i + 1}</div>
                    <input
                      data-testid={`module-title-${m.id}`}
                      value={m.title}
                      onChange={(e) => updateModuleField(i, "title", e.target.value)}
                      className="flex-1 px-2 py-1 border border-transparent hover:border-gris rounded text-sm font-medium text-marino"
                    />
                    <button data-testid={`save-module-${m.id}`} onClick={() => saveModule(m)} className="text-marino hover:text-naranja" title="Guardar"><Save className="w-4 h-4" /></button>
                    <button data-testid={`delete-module-${m.id}`} onClick={() => deleteModule(m.id)} className="text-red-600 hover:text-red-800" title="Eliminar módulo"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={addLesson} className="bg-white border border-gris/30 rounded-xl p-5">
            <h3 className="font-heading font-bold text-marino text-lg mb-3 flex items-center gap-2"><Plus className="w-5 h-5 text-naranja" /> Añadir nueva lección</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="new-lesson-title" required placeholder="Título" value={newLesson.title} onChange={(e) => setNewLesson({ ...newLesson, title: e.target.value })} className="px-3 py-2 border border-gris rounded-lg text-sm" />
              <input data-testid="new-lesson-order" type="number" placeholder="Orden" value={newLesson.order_index} onChange={(e) => setNewLesson({ ...newLesson, order_index: parseInt(e.target.value || 0) })} className="px-3 py-2 border border-gris rounded-lg text-sm" />
              <select
                data-testid="new-lesson-module"
                value={newLesson.module_id}
                onChange={(e) => setNewLesson({ ...newLesson, module_id: e.target.value })}
                className="sm:col-span-2 px-3 py-2 border border-gris rounded-lg text-sm"
              >
                <option value="">Sin módulo (General)</option>
                {modules.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
              </select>
              <input data-testid="new-lesson-video" placeholder="URL de video (YouTube/Vimeo embed)" value={newLesson.video_url} onChange={(e) => setNewLesson({ ...newLesson, video_url: e.target.value })} className="sm:col-span-2 px-3 py-2 border border-gris rounded-lg text-sm" />
              <textarea data-testid="new-lesson-content" placeholder="Contenido / texto de la lección" value={newLesson.content} onChange={(e) => setNewLesson({ ...newLesson, content: e.target.value })} rows={3} className="sm:col-span-2 px-3 py-2 border border-gris rounded-lg text-sm" />
            </div>
            <button data-testid="add-lesson-btn" className="mt-3 bg-naranja text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-naranjaHover">
              <Plus className="w-4 h-4 inline mr-1" /> Añadir lección
            </button>
          </form>

          <div className="space-y-3">
            {sortedLessons.map((l, idx) => (
              <div
                key={l.id}
                data-testid={`edit-lesson-${l.id}`}
                draggable
                onDragStart={() => onDragStart(idx)}
                onDragOver={(e) => onDragOver(e, idx)}
                onDrop={(e) => onDrop(e, idx)}
                onDragEnd={onDragEnd}
                className={`bg-white border rounded-xl p-5 transition-all ${
                  dragIdx === idx ? "opacity-40 border-naranja" : overIdx === idx && dragIdx !== null ? "border-naranja border-2 shadow-md" : "border-gris/30"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="cursor-grab active:cursor-grabbing text-gris hover:text-marino mt-1 flex-shrink-0" title="Arrastra para reordenar">
                    <GripVertical className="w-5 h-5" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-marino/10 text-marino flex items-center justify-center text-sm font-bold flex-shrink-0">{idx + 1}</div>
                  <div className="flex-1 space-y-3">
                    <div className="grid sm:grid-cols-6 gap-2">
                      <input data-testid={`lesson-title-${l.id}`} value={l.title} onChange={(e) => updateLessonField(lessons.indexOf(l), "title", e.target.value)} className="sm:col-span-5 px-3 py-2 border border-gris rounded-lg text-sm font-medium" />
                      <input data-testid={`lesson-order-${l.id}`} type="number" value={l.order_index || 0} onChange={(e) => updateLessonField(lessons.indexOf(l), "order_index", parseInt(e.target.value || 0))} className="px-3 py-2 border border-gris rounded-lg text-sm" />
                    </div>
                    <input data-testid={`lesson-video-${l.id}`} placeholder="URL video" value={l.video_url || ""} onChange={(e) => updateLessonField(lessons.indexOf(l), "video_url", e.target.value)} className="w-full px-3 py-2 border border-gris rounded-lg text-sm" />
                    <select
                      data-testid={`lesson-module-${l.id}`}
                      value={l.module_id || ""}
                      onChange={(e) => updateLessonField(lessons.indexOf(l), "module_id", e.target.value || null)}
                      className="w-full px-3 py-2 border border-gris rounded-lg text-sm bg-white"
                    >
                      <option value="">Sin módulo (General)</option>
                      {modules.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
                    </select>
                    <textarea data-testid={`lesson-content-${l.id}`} value={l.content || ""} onChange={(e) => updateLessonField(lessons.indexOf(l), "content", e.target.value)} rows={2} className="w-full px-3 py-2 border border-gris rounded-lg text-sm" />
                    <div className="flex gap-2">
                      <button data-testid={`save-lesson-${l.id}`} onClick={() => saveLesson(l)} className="bg-marino text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-marino/90 inline-flex items-center gap-1">
                        <Save className="w-3.5 h-3.5" /> Guardar
                      </button>
                      <button data-testid={`delete-lesson-${l.id}`} onClick={() => deleteLesson(l.id)} className="bg-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-red-700 inline-flex items-center gap-1">
                        <Trash2 className="w-3.5 h-3.5" /> Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {lessons.length === 0 && <p className="text-marino/60 text-center py-6">Este curso todavía no tiene lecciones. Añade la primera arriba.</p>}
            {lessons.length > 1 && (
              <p className="text-xs text-marino/60 text-center pt-2">💡 Arrastra las lecciones desde el ícono <GripVertical className="inline w-3.5 h-3.5" /> para reordenarlas</p>
            )}
          </div>
        </div>
      )}

      {/* Exam Editors */}
      {(tab === "initial" || tab === "final") && currentExam && (
        <div className="space-y-5">
          <div className="bg-marino/5 border border-marino/10 rounded-xl p-4 text-sm text-marino">
            {tab === "initial"
              ? "El examen diagnóstico se realiza al inicio del curso (1 solo intento). Sirve como punto de partida. Puedes usar puntaje de aprobación 0 si es solo diagnóstico."
              : "El examen final permite hasta 2 intentos. Aprobación con ≥ puntaje mínimo (típicamente 80%) genera certificado. Reprobar los 2 intentos bloquea el curso."}
          </div>

          <div className="bg-white border border-gris/30 rounded-xl p-5 flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium text-marino">Puntaje mínimo de aprobación (%):</label>
            <input
              data-testid={`passing-${tab}`}
              type="number" min="0" max="100"
              value={currentExam.passing_score}
              onChange={(e) => setCurrentExam({ ...currentExam, passing_score: parseInt(e.target.value || 0) })}
              className="w-24 px-3 py-2 border border-gris rounded-lg text-sm"
            />
            <div className="ml-auto flex gap-2">
              <button data-testid={`add-question-${tab}`} onClick={addQuestion} className="bg-gris text-marino px-3 py-2 rounded-lg text-sm font-medium hover:bg-grisHover">
                <Plus className="w-4 h-4 inline mr-1" /> Añadir pregunta
              </button>
              <button data-testid={`save-exam-${tab}`} onClick={saveExam} className="bg-naranja text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-naranjaHover">
                <Save className="w-4 h-4 inline mr-1" /> Guardar examen
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {currentExam.questions.map((q, qi) => (
              <div key={qi} data-testid={`question-${tab}-${qi}`} className="bg-white border border-gris/30 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-marino text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{qi + 1}</div>
                  <div className="flex-1 space-y-3">
                    <textarea
                      data-testid={`q-text-${tab}-${qi}`}
                      value={q.question}
                      onChange={(e) => updateQuestionField(qi, "question", e.target.value)}
                      rows={2}
                      placeholder="Enunciado de la pregunta"
                      className="w-full px-3 py-2 border border-gris rounded-lg text-sm font-medium focus:border-marino focus:outline-none"
                    />
                    <div className="space-y-2">
                      {q.options.map((opt, oi) => (
                        <div key={oi} className={`flex items-center gap-2 p-2 rounded-lg border ${q.correct_index === oi ? "border-naranja bg-naranja/5" : "border-gris/40"}`}>
                          <label className="flex items-center gap-1 text-xs text-marino/70 cursor-pointer">
                            <input
                              data-testid={`q-correct-${tab}-${qi}-${oi}`}
                              type="radio"
                              name={`correct-${tab}-${qi}`}
                              checked={q.correct_index === oi}
                              onChange={() => updateQuestionField(qi, "correct_index", oi)}
                            />
                            {q.correct_index === oi ? <CheckCircle2 className="w-4 h-4 text-naranja" /> : <span className="w-4 h-4 rounded-full border border-gris" />}
                          </label>
                          <input
                            data-testid={`q-opt-${tab}-${qi}-${oi}`}
                            value={opt}
                            onChange={(e) => updateOption(qi, oi, e.target.value)}
                            placeholder={`Opción ${oi + 1}`}
                            className="flex-1 px-2 py-1.5 border border-gris/60 rounded text-sm focus:border-marino focus:outline-none"
                          />
                          {q.options.length > 2 && (
                            <button onClick={() => removeOption(qi, oi)} className="text-red-500 hover:text-red-700" title="Quitar opción">
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      <div className="flex items-center justify-between">
                        <button data-testid={`add-opt-${tab}-${qi}`} onClick={() => addOption(qi)} className="text-marino/70 hover:text-naranja text-xs font-medium inline-flex items-center gap-1">
                          <Plus className="w-3.5 h-3.5" /> Añadir opción
                        </button>
                        <span className="text-xs text-marino/60">Marca la opción correcta con el radio</span>
                      </div>
                    </div>
                  </div>
                  <button data-testid={`remove-q-${tab}-${qi}`} onClick={() => removeQuestion(qi)} className="text-red-600 hover:text-red-800" title="Eliminar pregunta">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {currentExam.questions.length === 0 && <p className="text-marino/60 text-center py-6">Sin preguntas. Añade la primera con el botón de arriba.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
